import clsx from "clsx";
import { CheckIcon, CopyIcon } from "lucide-react";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useCopyContent } from "~/hooks/useCopyContent";
import { trackEvent } from "~/utils/analytics";

type CasePropertyItemProps = {
  label: string;
  value: string | ReactNode;
  className?: string;
  copyable?: boolean;
  truncate?: boolean;
};

export const CasePropertyItem: FC<CasePropertyItemProps> = ({
  label,
  value,
  className = "w-24",
  copyable = false,
  truncate = false,
}) => {
  const { isCopied, handleCopy } = useCopyContent();
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const onCopy = (content: string) => {
    trackEvent({
      eventName: "app/case/property.copy/click",
      properties: { label },
    });
    handleCopy(content);
  };

  useEffect(() => {
    if (truncate && textRef.current && typeof value === "string") {
      const isTextOverflowing =
        textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsOverflowing(isTextOverflowing);
    }
  }, [value, truncate]);

  const renderContent = (stringValue: string) => (
    <div className="self-stretch relative">
      <div
        ref={textRef}
        className={clsx(
          "text-foreground text-sm font-semibold leading-tight flex items-center gap-2",
          truncate && "truncate"
        )}
      >
        <span className={clsx(truncate && "truncate")}>{stringValue}</span>
        {copyable && stringValue && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 shrink-0">
            {isCopied ? (
              <CheckIcon className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <CopyIcon className="w-3.5 h-3.5" />
            )}
          </div>
        )}
      </div>
    </div>
  );

  const itemContent = (
    <div
      className={clsx(
        "flex-col justify-start items-start gap-1 inline-flex group p-1 -m-1",
        className,
        copyable &&
          typeof value === "string" &&
          "cursor-pointer rounded transition-colors"
      )}
      onClick={() => copyable && typeof value === "string" && onCopy(value)}
    >
      <div className="self-stretch text-muted-foreground text-xs font-normal leading-none">
        {label}
      </div>
      {typeof value === "string" && value.trim() !== "" ? (
        renderContent(value)
      ) : (
        <div className="flex items-center leading-tight">{value || "-"}</div>
      )}
    </div>
  );

  return typeof value === "string" && truncate && isOverflowing ? (
    <Tooltip>
      <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{value}</p>
      </TooltipContent>
    </Tooltip>
  ) : (
    itemContent
  );
};
